import {
    Box,
    Paper,
    Typography,
    IconButton,
    Tooltip
} from "@mui/material";
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    CalendarToday as CalendarIcon,
    StickyNote2 as NotesIcon
} from "@mui/icons-material";
import {useState} from "react";
import NoteDialog from "./NoteDialog";
import moment from "moment";
import {deleteNote, updateClientNote} from "../../services/TraineeService";
import {getErrorMessage} from "../../enums/ErrorMessages";
import {useMessageContext} from "../../contexts/MessageContext";
import {t as coachT} from "../dashboard/coach/coachTokens";
import {RTL} from "../../cache/CacheRtl";
import NoteAttachment from "./NoteAttachment";

const ClientNote = ({note, reload, coach = false}) => {
    const [editDialog, setEditDialog] = useState(false);
    const {notificationHandler} = useMessageContext();

    const handleDeleteNote = () => {
        deleteNote(note.id)
            .then(reload)
            .then(() => setEditDialog(false))
            .then(() => notificationHandler.success("פתק נמחק בהצלחה"))
            .catch(e => {
                notificationHandler.error(getErrorMessage(e.response?.data?.errorCode));
            })
    }

    const handleUpdateNote = (newNote) => {
        const body = {
            id: newNote.id,
            message: newNote.message,
            // Sent even when null: a null is the trainer taking the file OFF the note, and it is
            // the only way to remove one. The server writes what it is given.
            fileUrl: newNote.fileUrl ?? null,
            fileName: newNote.fileName ?? null,
        };

        updateClientNote(body)
            .then(reload)
            .then(() => setEditDialog(false))
            .then(() => notificationHandler.success("פתק נשמר בהצלחה"))
            .catch(e => {
                notificationHandler.error(getErrorMessage(e.response?.data?.errorCode));
            })
    }

    return (
        <>
            <NoteDialog
                approve={handleUpdateNote}
                initValue={note}
                close={() => setEditDialog(false)}
                isOpen={editDialog}
                coach={coach}
            />

            {note && coach && (
              <RTL>
                <Paper
                    dir="rtl"
                    sx={{
                        backgroundColor: coachT.white,
                        border: `1px solid ${coachT.border}`,
                        borderRadius: coachT.rs,
                        transition: "all .14s",
                        overflow: "hidden",
                        "&:hover": {
                            borderColor: coachT.green,
                            boxShadow: "0 2px 10px rgba(15,20,40,.06)",
                            transform: "translateY(-1px)",
                        }
                    }}
                    elevation={0}
                >
                    {/* Top row: date + actions */}
                    <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px 6px"}}>
                        <Box sx={{display: "flex", alignItems: "center", gap: 0.75}}>
                            <NotesIcon sx={{color: coachT.amber, fontSize: 16}}/>
                            <Typography sx={{fontSize: 11.5, fontWeight: 600, color: coachT.t3}}>
                                {moment.unix(note.createdDate).format("DD/MM/YY HH:mm")}
                            </Typography>
                        </Box>
                        <Box sx={{display: "flex", alignItems: "center", gap: 0.25}}>
                            <Tooltip title="עריכת הערה">
                                <IconButton onClick={() => setEditDialog(true)} size="small"
                                    sx={{color: coachT.t2, "&:hover": {background: coachT.greenLight, color: coachT.green}}}>
                                    <EditIcon fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="מחיקת הערה">
                                <IconButton onClick={handleDeleteNote} size="small"
                                    sx={{color: coachT.t2, "&:hover": {background: coachT.redLight, color: coachT.red}}}>
                                    <DeleteIcon fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                    {/* Note text */}
                    <Box sx={{padding: "0 14px 14px", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1.25}}>
                        {note.message && (
                            <Typography sx={{fontSize: 14, lineHeight: "21px", fontWeight: 500, color: coachT.text, whiteSpace: "pre-wrap", wordBreak: "break-word"}}>
                                {note.message}
                            </Typography>
                        )}
                        {note.fileUrl && (
                            <NoteAttachment fileUrl={note.fileUrl} fileName={note.fileName} />
                        )}
                    </Box>
                </Paper>
              </RTL>
            )}

            {note && !coach && (
              <RTL>
                <Paper
                    dir="rtl"
                    sx={{
                        backgroundColor: "#FFFFFF",
                        border: "1px solid #E2E8F0",
                        borderRadius: "8px",
                        transition: "all 0.2s ease",
                        "&:hover": {
                            borderColor: "#CBD5E1",
                            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)"
                        }
                    }}
                    elevation={0}
                >
                    {/* Note Header */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "12px 16px",
                            backgroundColor: "#F8FAFC",
                            borderBottom: "1px solid #E2E8F0"
                        }}
                    >
                        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                            <CalendarIcon sx={{ color: "#64748B", fontSize: "16px" }} />
                            <Typography sx={{
                                fontWeight: 500,
                                fontSize: "14px",
                                color: "#64748B"
                            }}>
                                {moment.unix(note.createdDate).format("DD/MM/YY HH:mm")}
                            </Typography>
                        </Box>

                        <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                            <Tooltip title="עריכת הערה">
                                <IconButton
                                    onClick={() => setEditDialog(true)}
                                    sx={{
                                        color: "#64748B",
                                        "&:hover": {
                                            backgroundColor: "#E2E8F0",
                                            color: "#475569"
                                        }
                                    }}
                                    size="small"
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="מחיקת הערה">
                                <IconButton
                                    onClick={handleDeleteNote}
                                    sx={{
                                        color: "#EF4444",
                                        "&:hover": {
                                            backgroundColor: "#FEF2F2",
                                            color: "#DC2626"
                                        }
                                    }}
                                    size="small"
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>

                    {/* Note Content */}
                    <Box sx={{ padding: "16px", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1.5 }}>
                        {note.message && (
                            <Typography sx={{
                                fontSize: "14px",
                                lineHeight: "20px",
                                color: "#1E293B",
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word"
                            }}>
                                {note.message}
                            </Typography>
                        )}
                        {note.fileUrl && (
                            <NoteAttachment fileUrl={note.fileUrl} fileName={note.fileName} />
                        )}
                    </Box>
                </Paper>
              </RTL>
            )}
        </>
    );
};

export default ClientNote;
